import React, { useState } from "react";
import { Link } from "react-router-dom";
import Starfield from "@/components/Starfield";
import { submitDataRequest } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";

const CONTACT = "support@stellarsteps.online";

export default function DataRequest() {
  const [email, setEmail] = useState("");
  const [classCode, setClassCode] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    const e = email.trim();
    if (!e || !e.includes("@") || !e.split("@").pop().includes(".")) {
      return toast.error("Please enter a valid email address");
    }
    setBusy(true);
    try {
      await submitDataRequest({ type: "deletion", email: e, classCode: classCode.trim(), details: details.trim() });
      setDone(true);
    } catch {
      toast.error("Could not send your request. Please email us instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#0B0D1B] text-white overflow-hidden">
      <Starfield />
      <div className="relative z-10 max-w-lg mx-auto p-5 sm:p-8 pb-28">
        <Link to="/" data-testid="datareq-back-link" className="text-slate-400 hover:text-white flex items-center gap-1 text-sm mb-5">
          <ArrowLeft className="w-4 h-4" /> Back to Stellar Steps
        </Link>

        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-6 h-6 text-cyan-300" />
          <h1 data-testid="datareq-title" className="font-heading text-3xl sm:text-4xl font-bold">Data Deletion Request</h1>
        </div>
        <p className="text-slate-400 mb-6 text-sm">
          Ask us to permanently delete personal data (a teacher account, a class, or a pupil). We action requests promptly and email confirmation to {CONTACT}.
        </p>

        {done ? (
          <div data-testid="datareq-success" className="bg-[#141738] border border-[#252B5C] rounded-3xl p-7 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <h2 className="font-heading text-2xl font-bold text-emerald-300 mb-1">Request received</h2>
            <p className="text-slate-300 text-sm">Thanks — we've logged your deletion request and notified our team. We'll follow up by email if we need anything else.</p>
            <Link to="/"><Button className="mt-5 bg-cyan-500 hover:bg-cyan-600 text-[#0B0D1B] font-heading font-bold min-h-[44px] w-full">Done</Button></Link>
          </div>
        ) : (
          <div className="bg-[#141738] border border-[#252B5C] rounded-3xl p-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-cyan-400 font-semibold">Your email</label>
              <Input
                data-testid="datareq-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="bg-[#0E1126] border-[#252B5C] text-white min-h-[48px] mt-1"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-cyan-400 font-semibold">Class mission code (optional)</label>
              <Input
                data-testid="datareq-classcode"
                value={classCode}
                onChange={(e) => setClassCode(e.target.value.toUpperCase())}
                placeholder="e.g. 6BKCD"
                className="bg-[#0E1126] border-[#252B5C] text-white min-h-[48px] mt-1"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-cyan-400 font-semibold">What should we delete?</label>
              <textarea
                data-testid="datareq-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="e.g. Please delete my class 'Year 5 Rockets' and all pupil data."
                rows={4}
                className="w-full bg-[#0E1126] border border-[#252B5C] text-white rounded-md p-3 mt-1 text-sm"
              />
            </div>
            <Button
              data-testid="datareq-submit"
              onClick={submit}
              disabled={busy}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-[#0B0D1B] font-heading font-bold min-h-[48px]"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send deletion request"}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              Prefer email? Write to <a className="text-cyan-300 underline" href={`mailto:${CONTACT}`}>{CONTACT}</a>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
